import {
  config,
  type Modality,
  ModalityType,
  useStores,
  type ApiToolCallModel,
} from '@littlebird/sdk';
import {
  isMeaningfulThought,
  isOpenableUrl,
  isTrustedUrl,
} from '@littlebird/ui-utils';
import { useParams } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useState, type ComponentType, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { ExternalLinkModal } from '@/components/modals/ExternalLinkModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { XStack, YStack } from '@/components/ui';
import { Favicon } from '@/components/ui/Favicon';
import { Markdown } from '@/components/ui/smoothdown/Markdown';
import { MCPIcon } from '@/features/mcp/MCPIcon';
import {
  isTerminalRunModality,
  parseTerminalRunArgs,
  rawTerminalRunArgs,
} from '@/features/sprites/terminal-run-card.utils';
import { TerminalRunCard } from '@/features/sprites/TerminalRunCard';
import { useTruncate } from '@/hooks/useTruncate';
import { userStore } from '@/stores/user.store';
import { NEW_CHAT_ID } from '@/utils/constants';
import { pathBasename } from '@/utils/files';
import { handleExternalUrlClick } from '@/utils/platform';

import {
  ArtifactGeneratingCard,
  isArtifactWriteModality,
} from './artifact-preview';
import { ReasoningStep } from './reasoning-step';
import { useReasoningStepContext } from './reasoning-step.context';
import {
  EDIT_TOOL_TYPES,
  extractLatestTodoWriteItems,
  getDomain,
  getEditToolChips,
  getModalityDescription,
  getModalityTitle,
  getStaticTodoWriteItems,
  getTodoWriteSummary,
  getUserContextChipGroups,
  getWebSearchQueries,
  integrationNameFromSlug,
  isSelfToolExecute,
  normalizeIntegrationName,
} from './reasoning.utils';
import { ReviewImages } from './ReviewImages';
import { descriptorFromModality } from './tool-descriptor';
import {
  ToolPayloadPreview,
  toolPayloadHasContent,
} from './ToolPayloadPreview';

// --- Constants ---

const FILTER_CHIP_CLASS =
  'border-dashed border-border-strongest bg-transparent text-foreground-subtle group-data-[compact=true]/reasoning:bg-transparent';
const SUBAGENT_SECTION_LABEL_CLASS = 'text-sm font-semibold text-foreground';
const SUBAGENT_FINAL_RESPONSE_EXTRA_KEY = 'subagent_final_response';
const READ_FILE_MAX_LINES = 200;

type FullShellCommandDisplayMode = 'all_users' | 'internal_only' | 'hidden';

// Temporary product knob until shell command visibility is wired to user policy.
const FULL_SHELL_COMMAND_DISPLAY_MODE: FullShellCommandDisplayMode =
  'internal_only';

const shouldShowShellCommandDetails = (
  mode: FullShellCommandDisplayMode,
  isInternalUser = userStore.isInternalUser
): boolean => {
  switch (mode) {
    case 'all_users':
      return true;
    case 'internal_only':
      return isInternalUser;
    case 'hidden':
      return false;
  }
};

const getModalityBodyDescription = (modality: Modality): string | undefined => {
  const description =
    'description' in modality ? modality.description : undefined;
  return typeof description === 'string'
    ? description.trim() || undefined
    : undefined;
};

// --- Icon helpers ---

const resolveToolIntegrationName = (modality: Modality): string | undefined => {
  if (isSelfToolExecute(modality)) return undefined;
  const slug = 'tool_slug' in modality ? modality.tool_slug : undefined;
  const name =
    'integration_name' in modality ? modality.integration_name : undefined;
  if (slug) return integrationNameFromSlug(slug);
  return name ? normalizeIntegrationName(name) : undefined;
};

const EDIT_TOOL_ICON_MAP: Partial<
  Record<ModalityType, ComponentType<{ className?: string }>>
> = {
  [ModalityType.ReadFile]: Icons.file,
  [ModalityType.EditFile]: Icons.edit,
  [ModalityType.ApplyPatch]: Icons.edit,
  [ModalityType.ListDirectory]: Icons.folderTree,
  [ModalityType.ListDir]: Icons.folderTree,
  [ModalityType.SearchFileContent]: Icons.search,
  [ModalityType.GrepFiles]: Icons.search,
  [ModalityType.WriteFile]: Icons.filePlus,
  [ModalityType.Bash]: Icons.mac,
  [ModalityType.RunSandbox]: Icons.mac,
};

const getEditToolIcon = (
  modality: Modality
): ComponentType<{ className?: string }> =>
  EDIT_TOOL_ICON_MAP[modality.type] ?? Icons.code;

// --- MCP modality icon components ---
//
// These are React components (not static icon types) so they can call
// useMCPCatalogQuery to resolve icon_url at render time.

const McpToolCallModalityIcon = ({
  modality,
  className,
}: {
  modality: Modality;
  className?: string;
}) => {
  if (modality.type !== ModalityType.McpToolCall) return null;
  return (
    <MCPIcon server={{ name: modality.server_name }} className={className} />
  );
};

/**
 * Icon for ToolExecute modalities. MCPIcon handles both standard
 * integrations (gmail, googlecalendar, etc., via the static integration map)
 * and MCP-sourced tools (catalog icon_url preferred, monogram via
 * getIntegrationIcon as final fallback).
 */
const IntegrationToolModalityIcon = ({
  modality,
  className,
}: {
  modality: Modality;
  className?: string;
}) => {
  const name = resolveToolIntegrationName(modality);
  if (!name) return <Icons.www className={className} />;
  return <MCPIcon server={{ name }} className={className} />;
};

// --- Chip building helpers ---

const buildUserContextGroups = (
  modality: Extract<Modality, { type: ModalityType.ChatWithUserContext }>
): ReactNode[] => {
  const { queryChips, filterChips } = getUserContextChipGroups(modality);
  return [
    queryChips.length > 0 && (
      <ReasoningStep.ChipContainer key="queries">
        {queryChips.map((q, i) => (
          <ReasoningStep.Chip key={`query-${q}-${i}`}>
            <Icons.search className="size-3 shrink-0 text-foreground-muted" />
            {q}
          </ReasoningStep.Chip>
        ))}
      </ReasoningStep.ChipContainer>
    ),
    filterChips.length > 0 && (
      <ReasoningStep.ChipContainer key="filters">
        {filterChips.map((f, i) => (
          <ReasoningStep.Chip
            key={`filter-${f}-${i}`}
            className={FILTER_CHIP_CLASS}
          >
            <Icons.listFilter className="size-3 shrink-0 text-foreground-muted" />
            {f}
          </ReasoningStep.Chip>
        ))}
      </ReasoningStep.ChipContainer>
    ),
  ].filter(Boolean) as ReactNode[];
};

const wrapGroups = (groups: ReactNode[]): ReactNode =>
  groups.length <= 1 ? (
    <>{groups}</>
  ) : (
    <YStack className="gap-2">{groups}</YStack>
  );

// --- Content render functions ---

const renderTextPreview = (text: string): ReactNode => (
  <div className="mt-2 text-sm text-foreground-muted">{text}</div>
);

const renderUserContextContent = (modality: Modality): ReactNode => {
  if (modality.type !== ModalityType.ChatWithUserContext) return null;
  return wrapGroups(buildUserContextGroups(modality));
};

const renderUserContextPreview = (modality: Modality): ReactNode => {
  if (modality.type !== ModalityType.ChatWithUserContext) return null;
  const groups = buildUserContextGroups(modality);
  if (groups.length === 0) return null;
  return (
    <div className="mt-2">
      {groups.length === 1 ? (
        groups[0]
      ) : (
        <YStack className="gap-2">{groups}</YStack>
      )}
    </div>
  );
};

const getRenderableWebSearchQueries = (modality: Modality): string[] =>
  getWebSearchQueries(modality).filter((query) => query.trim().length > 0);

const renderWebSearchContent = (modality: Modality): ReactNode => {
  if (
    modality.type !== ModalityType.ChatWithWebSearch &&
    modality.type !== ModalityType.ChatWithWebSearchMulti
  )
    return null;
  const queries = getRenderableWebSearchQueries(modality);
  if (queries.length === 0) return null;
  return (
    <ReasoningStep.ChipContainer>
      {queries.map((q, i) => (
        <ReasoningStep.Chip key={`${q}-${i}`}>
          <Icons.search className="size-3 shrink-0 text-foreground-muted" />
          {q}
        </ReasoningStep.Chip>
      ))}
    </ReasoningStep.ChipContainer>
  );
};

const renderWebSearchPreview = (modality: Modality): ReactNode => {
  const content = renderWebSearchContent(modality);
  if (!content) return null;
  return <div className="mt-2">{content}</div>;
};

const renderWebNavContent = (modality: Modality): ReactNode => {
  if (modality.type !== ModalityType.ChatWithWebNavigation) return null;
  const urls = Array.isArray(modality.urls) ? modality.urls : [];
  const openableUrls = urls.filter(
    (url: unknown): url is string =>
      typeof url === 'string' && isOpenableUrl(url)
  );
  if (openableUrls.length === 0) return null;
  return (
    <ReasoningStep.ChipContainer>
      {openableUrls.map((url) => (
        <ReasoningStep.Chip
          key={url}
          onClick={() => {
            const confirmExternalLinks = config.chat.confirm_external_links;
            if (isTrustedUrl(url) || !confirmExternalLinks) {
              handleExternalUrlClick(url);
            } else {
              ModalsManager.show(ExternalLinkModal, { url });
            }
          }}
        >
          <Favicon
            domain={getDomain(url)}
            className="size-3 shrink-0 rounded-sm"
            fallback={
              <Icons.link className="size-3 shrink-0 text-foreground-muted" />
            }
          />
          <span>{getDomain(url)}</span>
          <Icons.externalLink className="size-3 shrink-0 text-foreground-muted ml-auto" />
        </ReasoningStep.Chip>
      ))}
    </ReasoningStep.ChipContainer>
  );
};

const renderWebNavPreview = (modality: Modality): ReactNode => {
  const content = renderWebNavContent(modality);
  if (!content) return null;
  return <div className="mt-2">{content}</div>;
};

const isNotesModality = (
  modality: Modality
): modality is Extract<
  Modality,
  { type: ModalityType.ChatWithNotes | ModalityType.EditAssistantNotes }
> =>
  modality.type === ModalityType.ChatWithNotes ||
  modality.type === ModalityType.EditAssistantNotes;

const getNotesReplacement = (modality: Modality): string =>
  isNotesModality(modality) && typeof modality.replacement === 'string'
    ? modality.replacement
    : '';

const renderNotesContent = (modality: Modality): ReactNode => {
  const replacement = getNotesReplacement(modality).trim();
  if (!replacement) return null;
  return <span className="text-sm text-foreground-muted">{replacement}</span>;
};

const renderNotesPreview = (modality: Modality): ReactNode => {
  if (!isNotesModality(modality)) return null;
  const replacement = getNotesReplacement(modality);
  const truncated =
    !replacement || replacement.length === 0
      ? '...'
      : replacement.length > 70
        ? replacement.substring(0, 70) + '...'
        : replacement;
  return renderTextPreview(truncated);
};

const renderDescriptionContent = (
  modality: Modality,
  { isActive }: RenderContentOpts
): ReactNode => getModalityDescription(modality, isActive);

const renderImageGenerationContent = (_modality: Modality): ReactNode => {
  return null;
};

const renderImageGenerationPreview = (_modality: Modality): ReactNode => {
  return null;
};

const getSubagentArgLabel = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0
    ? value.trim().replaceAll('_', ' ')
    : null;

const getSubagentRoutingLabel = (
  modality: Modality,
  toolCall: ApiToolCallModel | undefined,
  isInternalUser: boolean
): string | undefined => {
  if (modality.type !== ModalityType.RunSubagent || !isInternalUser) {
    return undefined;
  }

  const subagentType = getSubagentArgLabel(toolCall?.arguments.subagent_type);
  const mode = getSubagentArgLabel(toolCall?.arguments.mode);
  const metadata = [subagentType, mode].filter(Boolean);
  return metadata.length > 0 ? `[${metadata.join(' / ')}]` : undefined;
};

const getSubagentPreviewTitle = (
  modality: Modality,
  isActive: boolean,
  toolCall?: ApiToolCallModel
): ReactNode => {
  const title = getModalityTitle(modality, isActive);
  const routingLabel = getSubagentRoutingLabel(
    modality,
    toolCall,
    userStore.isInternalUser
  );
  if (!routingLabel) return title;

  return (
    <span className="flex items-center gap-1.5 min-w-0">
      <span className="text-foreground-muted flex-shrink-0">
        {routingLabel}
      </span>
      <span className="truncate">{title}</span>
    </span>
  );
};

// run_sandbox lets the agent pick which files the user sees (`present`); surface them as a labeled
// chip group so the tool call is legible even though the underlying command stays internal-only.
const toStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : [];

const renderSandboxFileGroup = (
  label: string,
  paths: string[],
  Icon: ComponentType<{ className?: string }>,
  keyPrefix: string
): ReactNode => {
  if (paths.length === 0) return null;
  return (
    <ReasoningStep.ChipContainer key={keyPrefix}>
      <span className="self-center text-xs leading-none text-foreground-muted">
        {label}
      </span>
      {paths.map((path, i) => (
        <ReasoningStep.Chip key={`${keyPrefix}-${path}-${i}`}>
          <Icon className="size-3 shrink-0 text-foreground-muted" />
          {pathBasename(path)}
        </ReasoningStep.Chip>
      ))}
    </ReasoningStep.ChipContainer>
  );
};

const renderSandboxFileChips = (modality: Modality): ReactNode => {
  if (modality.type !== ModalityType.RunSandbox) return null;
  const present = toStringArray(modality.present);
  if (present.length === 0) return null;
  return renderSandboxFileGroup('Shown to you', present, Icons.file, 'present');
};

const getSandboxReviewFileIds = (modality: Modality): string[] =>
  modality.type === ModalityType.RunSandbox
    ? toStringArray(modality.review_file_ids)
    : [];

const renderSandboxReviewImages = (modality: Modality): ReactNode => {
  const reviewFileIds = getSandboxReviewFileIds(modality);
  if (reviewFileIds.length === 0) return null;
  return <ReviewImages fileIds={reviewFileIds} />;
};

// Shared chip row used by both the generic edit-tool renderers and the run_sandbox renderer.
const renderEditChipRow = (
  Icon: ComponentType<{ className?: string }>,
  context: string | undefined,
  chips: string[]
): ReactNode => {
  if (!context && chips.length === 0) return null;
  return (
    <ReasoningStep.ChipContainer>
      {context && (
        <span className="self-center text-xs leading-none text-foreground-muted truncate max-w-[200px]">
          {context}
        </span>
      )}
      {chips.map((chip, i) => (
        <ReasoningStep.Chip key={`${chip}-${i}`}>
          {i === 0 && (
            <Icon className="size-3 shrink-0 text-foreground-muted" />
          )}
          {chip}
        </ReasoningStep.Chip>
      ))}
    </ReasoningStep.ChipContainer>
  );
};

const renderEditToolContent = (
  modality: Modality,
  { toolCall }: RenderContentOpts
): ReactNode => {
  const Icon = getEditToolIcon(modality);
  const { context, chips } = getEditToolChips(
    modality,
    shouldShowShellCommandDetails(FULL_SHELL_COMMAND_DISPLAY_MODE)
  );
  const description = getModalityBodyDescription(modality);
  const descriptor = descriptorFromModality(modality, toolCall);
  // Match the limit the LLM actually saw — defaults to the client tool max.
  const readRequestedLimit =
    modality.type === ModalityType.ReadFile
      ? (modality.limit ?? READ_FILE_MAX_LINES)
      : undefined;

  return (
    <YStack className="gap-2">
      {description && (
        <span className="text-sm text-foreground-muted">{description}</span>
      )}
      {renderEditChipRow(Icon, context, chips)}
      {descriptor && (
        <ToolPayloadPreview
          descriptor={descriptor}
          readRequestedLimit={readRequestedLimit}
        />
      )}
    </YStack>
  );
};

const renderEditToolPreview = (modality: Modality): ReactNode => {
  // Prefer the agent-authored summary for live collapsed previews; fall back to
  // structured chips only when no summary was provided.
  const description = getModalityBodyDescription(modality);
  if (description) return renderTextPreview(description);

  const { context, chips } = getEditToolChips(
    modality,
    shouldShowShellCommandDetails(FULL_SHELL_COMMAND_DISPLAY_MODE)
  );
  if (chips.length === 0) return null;
  return (
    <div className="mt-2">
      {renderEditChipRow(getEditToolIcon(modality), context, chips)}
    </div>
  );
};

// run_sandbox gets its own renderer: the agent-authored description is promoted to the step title
// (see getModalityTitle), so it's never repeated in the body; the present file chips are surfaced
// alongside the normal command chips.
const renderSandboxContent = (
  modality: Modality,
  { toolCall }: RenderContentOpts
): ReactNode => {
  if (modality.type !== ModalityType.RunSandbox) return null;
  const reviewImages = renderSandboxReviewImages(modality);
  if (!userStore.isInternalUser) return reviewImages ?? <></>;
  const { context, chips } = getEditToolChips(
    modality,
    shouldShowShellCommandDetails(FULL_SHELL_COMMAND_DISPLAY_MODE)
  );
  const descriptor = descriptorFromModality(modality, toolCall);
  return (
    <YStack className="gap-2">
      {renderEditChipRow(getEditToolIcon(modality), context, chips)}
      {renderSandboxFileChips(modality)}
      {reviewImages}
      {descriptor && <ToolPayloadPreview descriptor={descriptor} />}
    </YStack>
  );
};

const renderSandboxPreview = (modality: Modality): ReactNode => {
  if (modality.type !== ModalityType.RunSandbox) return null;

  if (isArtifactWriteModality(modality)) return <ArtifactGeneratingCard />;

  if (!userStore.isInternalUser) return null;
  const sandboxFileChips = renderSandboxFileChips(modality);
  if (!sandboxFileChips) return null;
  return <div className="mt-2">{sandboxFileChips}</div>;
};

const renderSubagentContent = (
  modality: Modality,
  { isActive, toolCall }: RenderContentOpts
): ReactNode => {
  if (modality.type !== ModalityType.RunSubagent) return null;
  return (
    <RunSubagentContent
      modality={modality}
      isActive={isActive}
      toolCall={toolCall}
    />
  );
};

const getSubagentExtraString = (
  toolCall: ApiToolCallModel | undefined,
  key: string
): string => {
  const extra = toolCall?.extra;
  if (!extra) return '';
  const value = extra[key];
  return typeof value === 'string' ? value.trim() : '';
};

const getSubagentPrompt = (
  modality: Extract<Modality, { type: ModalityType.RunSubagent }>,
  toolCall?: ApiToolCallModel
): string => {
  const fromModality = modality.prompt?.trim();
  if (fromModality) return fromModality;

  const fromArgs = toolCall?.arguments?.prompt;
  return typeof fromArgs === 'string' ? fromArgs.trim() : '';
};

const RunSubagentContent = observer(
  ({
    modality,
    isActive,
    toolCall,
  }: {
    modality: Extract<Modality, { type: ModalityType.RunSubagent }>;
    isActive: boolean;
    toolCall?: ApiToolCallModel;
  }) => {
    const result =
      getSubagentExtraString(toolCall, SUBAGENT_FINAL_RESPONSE_EXTRA_KEY) ||
      toolCall?.result?.trim();
    if (!userStore.isInternalUser) return <></>;

    const prompt = getSubagentPrompt(modality, toolCall);
    const transcript =
      getSubagentExtraString(toolCall, 'subagent_transcript') ||
      getSubagentExtraString(toolCall, 'subagent_live_transcript');

    return (
      <YStack className="gap-3">
        {(prompt || isActive) && (
          <YStack className="gap-1.5">
            <span className={SUBAGENT_SECTION_LABEL_CLASS}>Prompt</span>
            {prompt ? (
              <Markdown
                className="text-sm text-foreground-muted uniform-text-size"
                id="subagent-prompt"
                content={prompt}
              />
            ) : (
              <span className="text-sm text-foreground-muted">
                Waiting for prompt...
              </span>
            )}
          </YStack>
        )}
        {(result || isActive) && (
          <YStack className="gap-1.5">
            <span className={SUBAGENT_SECTION_LABEL_CLASS}>Final answer</span>
            {result ? (
              <Markdown
                className="text-sm text-foreground-muted uniform-text-size"
                id="subagent-result"
                content={result}
              />
            ) : (
              <span className="text-sm text-foreground-muted">
                Waiting for result...
              </span>
            )}
          </YStack>
        )}
        {transcript && (
          <YStack className="gap-1.5">
            <span className={SUBAGENT_SECTION_LABEL_CLASS}>Subagent chat</span>
            <Markdown
              className="text-sm text-foreground-muted uniform-text-size"
              id="subagent-transcript"
              content={transcript}
            />
          </YStack>
        )}
      </YStack>
    );
  }
);

// --- Task/Todo helpers ---

type TaskItem = {
  id: string;
  content: string;
  status: string;
  summary?: string;
};

const TaskStatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'completed':
      return <Icons.squareCheck className="size-3.5 text-success-9" />;
    case 'in_progress':
      return (
        <Icons.square className="size-3.5 text-[var(--brand-jordy-blue)] animate-pulse" />
      );
    case 'cancelled':
      return <Icons.squareX className="size-3.5 text-danger-10" />;
    case 'pending':
    default:
      return <Icons.square className="size-3.5 text-foreground-muted" />;
  }
};

const TaskItemRow = ({ item }: { item: TaskItem }) => {
  const [open, setOpen] = useState(false);
  const isInternal = userStore.isInternalUser;
  const showSummary = isInternal && !!item.summary;
  const isCompleted = item.status === 'completed';
  const isCancelled = item.status === 'cancelled';
  const isPending = item.status === 'pending';

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left text-sm cursor-default"
        onClick={() => showSummary && setOpen((prev) => !prev)}
      >
        <XStack className="items-center gap-2 flex-1">
          <TaskStatusIcon status={item.status} />
          <span
            className={
              isCancelled
                ? 'text-foreground-subtle line-through'
                : isCompleted
                  ? 'text-foreground-subtle'
                  : isPending
                    ? 'text-foreground-muted'
                    : 'text-foreground'
            }
          >
            {item.content}
          </span>
        </XStack>
        {showSummary && (
          <Icons.chevronRight
            className={`size-3 text-foreground-subtle transition-transform ${open ? 'rotate-90' : ''}`}
          />
        )}
      </button>
      {open && item.summary && (
        <div className="ml-6 text-xs text-foreground-muted leading-relaxed whitespace-pre-wrap">
          {item.summary}
        </div>
      )}
    </div>
  );
};

function useLatestTasks(): TaskItem[] | null {
  const { chatId } = useParams({ strict: false });
  const threadId = chatId ?? NEW_CHAT_ID;
  const { messages: messagesStore } = useStores();
  const messages = messagesStore.getMessagesByThreadId(threadId);
  return extractLatestTodoWriteItems(messages);
}

// Shared UI for the expanded TodoWrite step
const TodoStepUI = ({
  items,
  isActive,
}: {
  items: TaskItem[];
  isActive: boolean;
}) => {
  if (items.length === 0) return null;

  const { doneCount, totalCount, headerItem } = getTodoWriteSummary(items);

  return (
    <>
      <ReasoningStep.Preview>
        <ReasoningStep.PreviewIcon>
          <Icons.list className="size-3.5" />
        </ReasoningStep.PreviewIcon>
        <ReasoningStep.PreviewText>
          {doneCount === totalCount ? (
            <span className="flex items-center gap-1.5 min-w-0">
              <span>Completed tasks</span>
              <span className="text-foreground-muted">
                [{doneCount}/{totalCount}]
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-1.5 min-w-0">
              <span>{isActive ? 'Updating tasks' : 'Tasks'}</span>
              {headerItem && (
                <span className="text-foreground-muted truncate">
                  {headerItem.content}
                </span>
              )}
              <span className="text-foreground-muted flex-shrink-0">
                [{doneCount}/{totalCount}]
              </span>
            </span>
          )}
        </ReasoningStep.PreviewText>
      </ReasoningStep.Preview>
      <ReasoningStep.Content>
        <YStack className="gap-1.5">
          {items.map((item) => (
            <TaskItemRow key={item.id} item={item} />
          ))}
        </YStack>
      </ReasoningStep.Content>
    </>
  );
};

// Shared UI for the collapsed TodoWrite preview
const TodoPreviewUI = ({ items }: { items: TaskItem[] }) => {
  if (items.length === 0) return null;

  const { doneCount, totalCount, headerItem } = getTodoWriteSummary(items);

  return (
    <span className="flex items-center gap-1.5 text-sm text-foreground-muted min-w-0 mt-2">
      {doneCount === totalCount ? (
        <>
          <span>Completed tasks</span>
          <span>
            [{doneCount}/{totalCount}]
          </span>
        </>
      ) : (
        <>
          {headerItem ? (
            <>
              <span className="flex-shrink-0">
                <TaskStatusIcon status={headerItem.status} />
              </span>
              <span className="truncate">{headerItem.content}</span>
            </>
          ) : (
            <span>Tasks</span>
          )}
          <span className="flex-shrink-0">
            [{doneCount}/{totalCount}]
          </span>
        </>
      )}
    </span>
  );
};

// Only subscribes to thread messages for the one live TodoWrite step
const LiveTodoWriteStep = observer(
  ({ modality, isActive }: { modality: Modality; isActive: boolean }) => {
    const latestTasks = useLatestTasks();
    const items = latestTasks ?? getStaticTodoWriteItems(modality);
    return <TodoStepUI items={items} isActive={isActive} />;
  }
);

const TodoWriteStep = observer(({ modality }: { modality: Modality }) => {
  const { isActive, isLiveTodo } = useReasoningStepContext();

  if (isLiveTodo) {
    return <LiveTodoWriteStep modality={modality} isActive={isActive} />;
  }
  return (
    <TodoStepUI items={getStaticTodoWriteItems(modality)} isActive={isActive} />
  );
});

const LiveTodoWritePreview = observer(
  ({ modality }: { modality: Modality }) => {
    const latestTasks = useLatestTasks();
    const items = latestTasks ?? getStaticTodoWriteItems(modality);
    return <TodoPreviewUI items={items} />;
  }
);

const TodoWritePreview = ({
  modality,
  isLiveTodo,
}: {
  modality: Modality;
  isLiveTodo?: boolean;
}) => {
  if (isLiveTodo) {
    return <LiveTodoWritePreview modality={modality} />;
  }
  return <TodoPreviewUI items={getStaticTodoWriteItems(modality)} />;
};

// --- Registry ---

interface RenderContentOpts {
  isActive: boolean;
  toolCall?: ApiToolCallModel;
  /** See ReasoningStepContextType.stepId. */
  stepId?: string;
}

type DetailContentOpts = RenderContentOpts & {
  isInternalUser: boolean;
};

const hasText = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().length > 0;

const hasDescriptionDetail = (
  modality: Modality,
  { isActive }: DetailContentOpts
): boolean => hasText(getModalityDescription(modality, isActive));

const hasUserContextDetail = (modality: Modality): boolean => {
  const { queryChips, filterChips } = getUserContextChipGroups(modality);
  return queryChips.length > 0 || filterChips.length > 0;
};

const hasWebSearchDetail = (modality: Modality): boolean =>
  getRenderableWebSearchQueries(modality).length > 0;

const hasWebNavigationDetail = (modality: Modality): boolean =>
  modality.type === ModalityType.ChatWithWebNavigation &&
  Array.isArray(modality.urls) &&
  modality.urls.some(
    (url: unknown) => typeof url === 'string' && isOpenableUrl(url)
  );

const hasNotesDetail = (modality: Modality): boolean =>
  isNotesModality(modality) && hasText(modality.replacement);

const hasTodoDetail = (modality: Modality): boolean =>
  getStaticTodoWriteItems(modality).length > 0;

const hasEditToolDetail = (
  modality: Modality,
  { toolCall, isInternalUser }: DetailContentOpts,
  includeDescription = true
): boolean => {
  if (includeDescription && hasText(getModalityBodyDescription(modality))) {
    return true;
  }

  const { context, chips } = getEditToolChips(
    modality,
    shouldShowShellCommandDetails(
      FULL_SHELL_COMMAND_DISPLAY_MODE,
      isInternalUser
    )
  );
  if (hasText(context) || chips.some((chip) => hasText(chip))) return true;

  const descriptor = descriptorFromModality(modality, toolCall);
  return Boolean(descriptor && toolPayloadHasContent(descriptor));
};

const hasSandboxDetail = (
  modality: Modality,
  opts: DetailContentOpts
): boolean => {
  if (modality.type !== ModalityType.RunSandbox) return false;
  if (getSandboxReviewFileIds(modality).length > 0) return true;
  if (!opts.isInternalUser) return false;
  if (toStringArray(modality.present).some((path) => hasText(path))) {
    return true;
  }
  return hasEditToolDetail(modality, opts, false);
};

const hasSubagentDetail = (
  modality: Modality,
  { isActive, toolCall, isInternalUser }: DetailContentOpts
): boolean => {
  if (modality.type !== ModalityType.RunSubagent || !isInternalUser) {
    return false;
  }
  return Boolean(
    isActive ||
    getSubagentPrompt(modality, toolCall) ||
    toolCall?.result?.trim() ||
    getSubagentExtraString(toolCall, SUBAGENT_FINAL_RESPONSE_EXTRA_KEY) ||
    getSubagentExtraString(toolCall, 'subagent_transcript') ||
    getSubagentExtraString(toolCall, 'subagent_live_transcript')
  );
};

type ModalityRenderer = {
  icon: ComponentType<{ className?: string }>;
  getIcon?: (
    modality: Modality
  ) =>
    | { icon: ComponentType<{ className?: string }>; iconSize?: string }
    | { noIcon: true };
  /**
   * When set, rendered in place of the static `icon` / `getIcon` resolution.
   * Use this for icons that require React hooks (e.g. catalog look-ups).
   * Receives the full `modality` and a `className` carrying the resolved size.
   */
  ModalityIcon?: ComponentType<{ modality: Modality; className?: string }>;
  iconSize?: string;
  renderContent?: (modality: Modality, opts: RenderContentOpts) => ReactNode;
  hasDetailContent?: (modality: Modality, opts: DetailContentOpts) => boolean;
  renderPreview?: (modality: Modality) => ReactNode;
  Component?: ComponentType<{ modality: Modality }>;
  PreviewComponent?: ComponentType<{
    modality: Modality;
    isLiveTodo?: boolean;
  }>;
  // Render a preview-only step with no expandable body.
  bodyless?: boolean;
  /**
   * When true, the modality description is rendered inline on the same line as
   * the title ("Used Notion · Fetch") instead of in a separate Content block
   * below it. The Content block is suppressed entirely.
   */
  inlineDescription?: boolean;
};

const editToolIconConfig = (modality: Modality) => ({
  icon: getEditToolIcon(modality),
});

const MODALITY_REGISTRY: Partial<Record<ModalityType, ModalityRenderer>> = {
  [ModalityType.ChatWithUserContext]: {
    icon: Icons.search,
    renderContent: renderUserContextContent,
    hasDetailContent: hasUserContextDetail,
    renderPreview: renderUserContextPreview,
  },
  [ModalityType.ChatWithWebSearch]: {
    icon: Icons.webSearch,
    renderContent: renderWebSearchContent,
    hasDetailContent: hasWebSearchDetail,
    renderPreview: renderWebSearchPreview,
  },
  [ModalityType.ChatWithWebSearchMulti]: {
    icon: Icons.webSearch,
    renderContent: renderWebSearchContent,
    hasDetailContent: hasWebSearchDetail,
    renderPreview: renderWebSearchPreview,
  },
  [ModalityType.ChatWithWebNavigation]: {
    icon: Icons.webSearch,
    renderContent: renderWebNavContent,
    hasDetailContent: hasWebNavigationDetail,
    renderPreview: renderWebNavPreview,
  },
  [ModalityType.ChatWithNotes]: {
    icon: Icons.memory,
    renderContent: renderNotesContent,
    hasDetailContent: hasNotesDetail,
    renderPreview: renderNotesPreview,
  },
  [ModalityType.EditAssistantNotes]: {
    icon: Icons.memory,
    renderContent: renderNotesContent,
    hasDetailContent: hasNotesDetail,
    renderPreview: renderNotesPreview,
  },
  [ModalityType.SelfKnowledge]: {
    icon: Icons.selfKnowledge,
    renderContent: renderDescriptionContent,
    hasDetailContent: hasDescriptionDetail,
  },
  [ModalityType.ImageGeneration]: {
    icon: Icons.image,
    renderContent: renderImageGenerationContent,
    renderPreview: renderImageGenerationPreview,
  },
  [ModalityType.WorldModel]: {
    icon: Icons.www,
    renderContent: renderDescriptionContent,
    hasDetailContent: hasDescriptionDetail,
  },
  [ModalityType.Route]: {
    icon: Icons.sort,
    renderContent: renderDescriptionContent,
    hasDetailContent: hasDescriptionDetail,
  },
  [ModalityType.TodoWrite]: {
    icon: Icons.list,
    Component: TodoWriteStep,
    hasDetailContent: hasTodoDetail,
    PreviewComponent: TodoWritePreview,
  },
  [ModalityType.McpToolCall]: {
    icon: Icons.www,
    ModalityIcon: McpToolCallModalityIcon,
    inlineDescription: true,
  },
  [ModalityType.ToolExecute]: {
    icon: Icons.www,
    ModalityIcon: IntegrationToolModalityIcon,
    inlineDescription: true,
  },
  [ModalityType.RunSubagent]: {
    icon: Icons.research,
    renderContent: renderSubagentContent,
    hasDetailContent: hasSubagentDetail,
  },
  [ModalityType.GetScreenContext]: {
    icon: Icons.system,
    bodyless: true,
  },
};

// Register all edit tool types
for (const type of EDIT_TOOL_TYPES) {
  MODALITY_REGISTRY[type as ModalityType] = {
    icon: Icons.code,
    getIcon: editToolIconConfig,
    renderContent: renderEditToolContent,
    hasDetailContent: hasEditToolDetail,
    renderPreview: renderEditToolPreview,
  };
}

// run_sandbox is an edit-tool type but renders its own present chips and title — override the
// generic edit-tool entry registered by the loop above.
MODALITY_REGISTRY[ModalityType.RunSandbox] = {
  icon: Icons.code,
  getIcon: editToolIconConfig,
  renderContent: renderSandboxContent,
  hasDetailContent: hasSandboxDetail,
  renderPreview: renderSandboxPreview,
};

// Agent-run shell commands (device `terminal` toolkit) arrive as generic
// tool_execute steps; they get a dedicated card instead of the integration pill.
const TERMINAL_RUN_RENDERER: ModalityRenderer = {
  icon: Icons.terminal,
  renderContent: (modality, { toolCall, stepId }) => (
    <TerminalRunCard modality={modality} toolCall={toolCall} stepId={stepId} />
  ),
  // Unparseable arguments still get a card (showing what was sent), so a step
  // is never a bare Used Terminal with nothing behind it.
  hasDetailContent: (modality, { toolCall }) =>
    parseTerminalRunArgs(modality, toolCall) !== null ||
    rawTerminalRunArgs(modality, toolCall) !== null,
  // No renderPreview on purpose: the step reads "Used Terminal" and the command
  // lives in the expanded card, so a collapsed chip would only repeat it. Other
  // integrations do show a chip; this one departs from that to keep chat quiet.
};

const getModalityRenderer = (
  modality: Modality
): ModalityRenderer | undefined =>
  isTerminalRunModality(modality)
    ? TERMINAL_RUN_RENDERER
    : MODALITY_REGISTRY[modality.type];

// eslint-disable-next-line react-refresh/only-export-components
export const getModalityDisplayLabelParts = (
  modality: Modality,
  toolCall: ApiToolCallModel | undefined,
  isActive: boolean,
  isInternalUser: boolean
): string[] | undefined => {
  const renderer = getModalityRenderer(modality);
  const title = getModalityTitle(modality, isActive);
  if (!renderer) return [title];

  const routingLabel = getSubagentRoutingLabel(
    modality,
    toolCall,
    isInternalUser
  );
  const inlineDescription = renderer.inlineDescription
    ? getModalityDescription(modality, isActive)
    : undefined;
  return [routingLabel, title, inlineDescription].filter(
    (part): part is string => Boolean(part)
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const modalityHasDetailContent = (
  modality: Modality,
  opts: DetailContentOpts
): boolean => {
  const renderer = getModalityRenderer(modality);
  if (!renderer || renderer.bodyless || renderer.inlineDescription) {
    return false;
  }
  return renderer.hasDetailContent?.(modality, opts) ?? false;
};

// eslint-disable-next-line react-refresh/only-export-components
export const renderModalityExpandedContent = (
  modality: Modality,
  opts: RenderContentOpts
): ReactNode =>
  getModalityRenderer(modality)?.renderContent?.(modality, opts) ?? null;

// --- Public components ---

const THOUGHT_PREVIEW_LINES = 3;

export const ThoughtContent = observer(
  ({ thought, id }: { thought: string; id: string }) => {
    if (!isMeaningfulThought(thought)) return null;

    return <ThoughtContentBody thought={thought} id={id} />;
  }
);

const ThoughtContentBody = ({
  thought,
  id,
}: {
  thought: string;
  id: string;
}) => {
  const [needsTruncation, setNeedsTruncation] = useState<boolean | undefined>(
    undefined
  );
  const { truncateElementRef, isExpanded, toggleExpand } = useTruncate({
    maxLines: THOUGHT_PREVIEW_LINES,
    onTruncationChange: setNeedsTruncation,
  });

  return (
    <YStack className="gap-2">
      <div
        ref={truncateElementRef}
        className="text-sm text-foreground-muted uniform-text-size"
      >
        <Markdown
          className="text-sm text-foreground-muted uniform-text-size"
          key={`${id}-thought-block`}
          id={`${id}-thought-block`}
          content={thought}
        />
      </div>
      {needsTruncation && (
        <button
          type="button"
          className="text-foreground-subtle hover:text-foreground-muted text-xs self-start"
          onClick={toggleExpand}
        >
          Show {isExpanded ? 'less' : 'more'}
        </button>
      )}
    </YStack>
  );
};

export const ModalityStepContent = observer(
  ({ modality }: { modality: Modality }) => {
    const { isActive, toolCall, stepId } = useReasoningStepContext();
    const renderer = getModalityRenderer(modality);
    const title = getModalityTitle(modality, isActive);
    if (!renderer) {
      return (
        <>
          <ReasoningStep.Preview>
            <ReasoningStep.PreviewIcon>
              <Icons.www className="size-3.5" />
            </ReasoningStep.PreviewIcon>
            <ReasoningStep.PreviewText title={title}>
              {title}
            </ReasoningStep.PreviewText>
          </ReasoningStep.Preview>
          <ReasoningStep.Content />
        </>
      );
    }

    if (renderer.Component) {
      return <renderer.Component modality={modality} />;
    }

    if (
      !renderer.bodyless &&
      !renderer.inlineDescription &&
      !renderer.renderContent
    )
      return null;

    const content =
      renderer.bodyless || renderer.inlineDescription
        ? null
        : (renderer.renderContent?.(modality, {
            isActive,
            toolCall,
            stepId,
          }) ?? null);

    const iconSize = renderer.iconSize ?? 'size-3.5';
    const inlineDesc = renderer.inlineDescription
      ? getModalityDescription(modality, isActive)
      : undefined;
    const rowTitle = inlineDesc ? `${title} · ${inlineDesc}` : title;

    // Prefer ModalityIcon (hook-capable component) over static icon resolution.
    if (renderer.ModalityIcon) {
      return (
        <>
          <ReasoningStep.Preview>
            <ReasoningStep.PreviewIcon>
              <renderer.ModalityIcon modality={modality} className={iconSize} />
            </ReasoningStep.PreviewIcon>
            <ReasoningStep.PreviewText title={rowTitle}>
              {getSubagentPreviewTitle(modality, isActive, toolCall)}
              {inlineDesc && (
                <span className="text-foreground-muted"> · {inlineDesc}</span>
              )}
            </ReasoningStep.PreviewText>
          </ReasoningStep.Preview>
          <ReasoningStep.Content>{content}</ReasoningStep.Content>
        </>
      );
    }

    const dynamic = renderer.getIcon?.(modality);
    const hasNoIcon =
      dynamic !== undefined && 'noIcon' in dynamic && dynamic.noIcon === true;
    const Icon = hasNoIcon
      ? null
      : dynamic && 'icon' in dynamic
        ? dynamic.icon
        : renderer.icon;
    const resolvedIconSize =
      Icon && dynamic && 'iconSize' in dynamic && dynamic.iconSize
        ? dynamic.iconSize
        : Icon
          ? iconSize
          : iconSize;

    return (
      <>
        <ReasoningStep.Preview>
          {Icon ? (
            <ReasoningStep.PreviewIcon>
              <Icon className={resolvedIconSize} />
            </ReasoningStep.PreviewIcon>
          ) : null}
          <ReasoningStep.PreviewText title={rowTitle}>
            {getSubagentPreviewTitle(modality, isActive, toolCall)}
            {inlineDesc && (
              <span className="text-foreground-muted"> · {inlineDesc}</span>
            )}
          </ReasoningStep.PreviewText>
        </ReasoningStep.Preview>
        <ReasoningStep.Content>{content}</ReasoningStep.Content>
      </>
    );
  }
);

export const ModalityPreviewContent = observer(
  ({ modality, isLiveTodo }: { modality: Modality; isLiveTodo?: boolean }) => {
    const renderer = getModalityRenderer(modality);

    if (renderer?.PreviewComponent) {
      return (
        <renderer.PreviewComponent
          modality={modality}
          isLiveTodo={isLiveTodo}
        />
      );
    }

    if (renderer?.renderPreview) {
      return <>{renderer.renderPreview(modality)}</>;
    }

    if (renderer?.inlineDescription) return null;
    const description = getModalityDescription(modality, true);
    return description ? renderTextPreview(description) : null;
  }
);
